import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CopyButton,
  Group,
  Modal,
  NativeSelect,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconCopy } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useMe } from '../api/session';
import {
  useAdminUser,
  useDisableAdmin,
  useEnableAdmin,
  usePromoteSuperAdmin,
  useRegenerateToken,
  useUpdateAdmin,
  type AssignableAdminRole,
  type InviteResult,
} from '../api/admin-users';

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  pending: 'Pending',
  disabled: 'Disabled',
};

export function AdminUserEditPage() {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const me = useMe();
  const isSelf = me.data?.id === id;

  const existing = useAdminUser(id);
  const updateAdmin = useUpdateAdmin(id);
  const disableAdmin = useDisableAdmin(id);
  const enableAdmin = useEnableAdmin(id);
  const regenerate = useRegenerateToken(id);
  const promote = usePromoteSuperAdmin(id);

  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<AssignableAdminRole>('support');
  const [error, setError] = useState<string | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [regenResult, setRegenResult] = useState<InviteResult | null>(null);
  const [confirmPromoteOpen, setConfirmPromoteOpen] = useState(false);
  const [promoteConfirmEmail, setPromoteConfirmEmail] = useState('');
  const [promoteReason, setPromoteReason] = useState('');
  const previousUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (existing.data) {
      setDisplayName(existing.data.displayName);
      if (existing.data.role !== 'super_admin') {
        setRole(existing.data.role);
      }
      if (previousUserIdRef.current !== existing.data.id) {
        setConfirmPromoteOpen(false);
        setPromoteConfirmEmail('');
        setPromoteReason('');
      }
      previousUserIdRef.current = existing.data.id;
    }
  }, [existing.data]);

  if (existing.isLoading) {
    return <Text c="dimmed">Loading…</Text>;
  }
  if (existing.isError || !existing.data) {
    return <Text c="red">Admin user not found.</Text>;
  }

  const user = existing.data;

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const trimmed = displayName.trim();
    if (!trimmed) {
      setError('Display name is required.');
      return;
    }
    const patch: { displayName?: string; role?: AssignableAdminRole } = {};
    if (trimmed !== user.displayName) patch.displayName = trimmed;
    if (!isSelf && user.role !== 'super_admin' && role !== user.role) patch.role = role;
    if (Object.keys(patch).length === 0) {
      setError('Nothing to update.');
      return;
    }
    updateAdmin.mutate(patch, {
      onSuccess: () => navigate('/admin-users'),
      onError: (err) => setError(formatError(err)),
    });
  };

  const handleDisable = () => {
    disableAdmin.mutate(undefined, {
      onSuccess: () => {
        setConfirmDisable(false);
        existing.refetch();
      },
      onError: (err) => {
        setConfirmDisable(false);
        setError(formatError(err));
      },
    });
  };

  const handleEnable = () => {
    enableAdmin.mutate(undefined, {
      onSuccess: () => existing.refetch(),
      onError: (err) => setError(formatError(err)),
    });
  };

  const handleRegenerate = () => {
    regenerate.mutate(undefined, {
      onSuccess: (r) => setRegenResult(r),
      onError: (err) => setError(formatError(err)),
    });
  };

  const handlePromote = () => {
    promote.mutate(
      { reason: promoteReason.trim(), confirmEmail: promoteConfirmEmail },
      {
        onSuccess: () => {
          setConfirmPromoteOpen(false);
          setPromoteConfirmEmail('');
          setPromoteReason('');
          existing.refetch();
        },
        onError: (err) => {
          setConfirmPromoteOpen(false);
          setError(formatError(err));
        },
      },
    );
  };

  return (
    <Stack gap="lg">
      <Title order={2}>Edit {user.email}</Title>

      <Card shadow="sm" padding="lg" maw={600}>
        <Box component="form" onSubmit={handleSave} noValidate>
          <Stack gap="md">
            {error && (
              <Alert color="red" icon={<IconAlertTriangle size={18} />}>
                {error}
              </Alert>
            )}
            <TextInput label="Email" value={user.email} disabled />
            <TextInput
              label="Display name"
              required
              value={displayName}
              onChange={(e) => setDisplayName(e.currentTarget.value)}
              disabled={updateAdmin.isPending}
            />
            {user.role === 'super_admin' ? (
              <TextInput
                label="Role"
                value="super_admin"
                disabled
                description="Highest privilege tier — cannot be changed here."
              />
            ) : (
              <NativeSelect
                label="Role"
                required
                data={[
                  { value: 'support', label: 'support' },
                  { value: 'admin', label: 'admin' },
                ]}
                value={role}
                onChange={(e) => setRole(e.currentTarget.value as AssignableAdminRole)}
                disabled={isSelf || updateAdmin.isPending}
                description={isSelf ? 'Cannot change your own role.' : undefined}
              />
            )}
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" component={Link} to="/admin-users">
                Cancel
              </Button>
              <Button type="submit" loading={updateAdmin.isPending}>
                Update
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>

      <Card shadow="sm" padding="lg" maw={600} withBorder>
        <Stack gap="sm">
          <Group gap="xs">
            <Text fw={500}>Status:</Text>
            <Badge>{STATUS_LABEL[user.status]}</Badge>
          </Group>

          {user.status === 'pending' && (
            <>
              <Text size="sm" c="dimmed">
                User has not yet activated their account. Regenerate the setup link if the old one
                was lost.
              </Text>
              <Group>
                <Button variant="default" onClick={handleRegenerate} loading={regenerate.isPending}>
                  Regenerate setup link
                </Button>
                {!isSelf && (
                  <Button color="red" onClick={() => setConfirmDisable(true)}>
                    Cancel invitation
                  </Button>
                )}
              </Group>
            </>
          )}

          {user.status === 'active' && !isSelf && (
            <>
              <Text size="sm" c="dimmed">
                Disabling clears the user's sessions and prevents future logins.
              </Text>
              <Button color="red" onClick={() => setConfirmDisable(true)}>
                Disable user
              </Button>
            </>
          )}

          {user.status === 'disabled' && (
            <>
              <Text size="sm" c="dimmed">
                User is disabled. Re-enabling restores login.
              </Text>
              <Button onClick={handleEnable} loading={enableAdmin.isPending}>
                Re-enable user
              </Button>
            </>
          )}
        </Stack>
      </Card>

      {!isSelf && user.role === 'admin' && me.data?.role === 'super_admin' && (
        <Card
          shadow="sm"
          padding="lg"
          maw={600}
          withBorder
          style={{ borderColor: 'var(--mantine-color-red-6)' }}
        >
          <Stack gap="sm">
            <Text fw={500} c="red">
              Promote to super admin
            </Text>
            <Text size="sm" c="dimmed">
              Grants the platform's highest privilege tier, including patient erasure and
              pseudonymisation.
            </Text>
            <Group>
              <Button color="red" variant="outline" onClick={() => setConfirmPromoteOpen(true)}>
                Promote to super admin
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {regenResult && (
        <Card shadow="sm" padding="lg" withBorder maw={720}>
          <Stack gap="md">
            <Title order={4}>New setup link</Title>
            <Text
              ff="monospace"
              size="sm"
              p="sm"
              style={{ wordBreak: 'break-all', background: 'var(--mantine-color-gray-0)' }}
            >
              {regenResult.setupUrl}
            </Text>
            <Group>
              <CopyButton value={regenResult.setupUrl} timeout={2000}>
                {({ copied, copy }) => (
                  <Button
                    onClick={copy}
                    leftSection={copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                    variant="default"
                  >
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                )}
              </CopyButton>
              <Button onClick={() => setRegenResult(null)}>Dismiss</Button>
            </Group>
          </Stack>
        </Card>
      )}

      <Modal
        opened={confirmDisable}
        onClose={() => setConfirmDisable(false)}
        title="Disable this user?"
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <Text size="sm">
            The user's sessions will be invalidated and they won't be able to sign in. You can
            re-enable them later.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setConfirmDisable(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={handleDisable} loading={disableAdmin.isPending}>
              Confirm disable
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={confirmPromoteOpen}
        onClose={() => setConfirmPromoteOpen(false)}
        title="Promote to super admin?"
        centered
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            This grants {user.email} the platform's highest privilege tier, including patient
            erasure and pseudonymisation. This cannot be undone from this screen.
          </Alert>
          <TextInput
            label={`Type the user's email to confirm (${user.email})`}
            value={promoteConfirmEmail}
            onChange={(e) => setPromoteConfirmEmail(e.currentTarget.value)}
            disabled={promote.isPending}
          />
          <Textarea
            label="Reason (required, logged)"
            required
            value={promoteReason}
            onChange={(e) => setPromoteReason(e.currentTarget.value)}
            disabled={promote.isPending}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="subtle"
              onClick={() => setConfirmPromoteOpen(false)}
              disabled={promote.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              loading={promote.isPending}
              disabled={promoteConfirmEmail !== user.email || promoteReason.trim().length === 0}
              onClick={handlePromote}
            >
              Confirm promotion
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return 'Validation failed. Check the highlighted fields.';
    if (err.status === 401) return 'Your session has expired. Please sign in again.';
    if (err.status === 403) return 'Your account does not have permission for this action.';
    if (err.status === 404) return 'Admin user not found.';
    if (err.status === 409) return 'Cannot leave the platform without an active admin.';
  }
  return 'Failed to update.';
}
